/*
 * dashcam/capture.js — camera → canvas with the stamp → H.264 → records.
 *
 * Why the canvas: the stamp (time, speed, coordinates, plate) has to be IN the
 * picture, not next to it, so every frame is drawn onto a canvas, stamped, and
 * the canvas is what the encoder reads. The same canvas is the preview, so
 * what the driver sees is exactly what is recorded.
 *
 * Why WebCodecs and not MediaRecorder: a loop recorder cuts an arbitrary
 * minute out of a long recording and saves it as its own file. MediaRecorder
 * produces one container per recording that cannot be cut without re-encoding;
 * WebCodecs hands over every encoded frame separately, with a keyframe every
 * second, and any stretch between two keyframes is a playable file of its own
 * (see ring.js and mux.js).
 *
 * The frame pump is a timer at the target rate, not requestVideoFrameCallback:
 * in the dimmed screen mode the video element is covered, and a callback that
 * only fires for PAINTED frames could go quiet while the camera runs. The
 * recorder only ever runs in a visible page (it stops itself on hide — the
 * browser takes the camera away there anyway), so the timer is not throttled.
 *
 * Audio (off by default) goes through the shared PCM worklet and the AAC door
 * (window.AacAccessUnit.encoder) — a bare AudioEncoder writes ADTS-framed AAC
 * on some Chromium builds, which Windows players open without sound.
 */

import { KEY_EVERY_US } from './ring.js';
import { videoTap, audioTap } from './loop-writer.js';
import { drawStamp } from './overlay.js';

/** Bits per second by the short side, at 30 fps. Chosen for a road scene
 *  (lots of motion, fine detail in plates) at the size a phone keeps cool. */
export const BITRATE = Object.freeze({ 480: 1800000, 720: 3500000, 1080: 6500000 });

export function bitrateFor(short, fps) {
    const b = BITRATE[short] || (short >= 1000 ? BITRATE[1080] : short >= 700 ? BITRATE[720] : BITRATE[480]);
    return Math.round(b * Math.min(1, (fps || 30) / 30));
}

/**
 * Output size for a requested short side, from the camera's real frame:
 * never upscaled, both sides even (every H.264 encoder demands it).
 */
export function outputSize(vw, vh, short) {
    if (!vw || !vh) return null;
    const camShort = Math.min(vw, vh);
    const s = Math.min(short, camShort);
    const k = s / camShort;
    const even = (x) => Math.max(2, Math.round(x / 2) * 2);
    return { w: even(vw * k), h: even(vh * k) };
}

function avcCodec(profile, w, h) {
    // level by picture size at 30 fps: 3.1 up to 720p, 4.0 up to 1080p, 5.1 above
    const px = w * h;
    const level = px <= 921600 ? '1f' : px <= 2228224 ? '28' : '33';
    return 'avc1.' + profile + level;
}

/** First configuration the browser says it can encode: H.264 High → Main → Baseline, then VP9. */
export async function pickVideoConfig(w, h, fps, bitrate) {
    if (typeof VideoEncoder === 'undefined') return null;
    const cands = [
        { mux: 'avc', codec: avcCodec('6400', w, h) },
        { mux: 'avc', codec: avcCodec('4d00', w, h) },
        { mux: 'avc', codec: avcCodec('42e0', w, h) },
        { mux: 'vp9', codec: 'vp09.00.40.08' },
    ];
    for (const c of cands) {
        for (const hw of ['prefer-hardware', 'no-preference']) {
            const cfg = { codec: c.codec, width: w, height: h, bitrate, framerate: fps, latencyMode: 'realtime', bitrateMode: 'variable', hardwareAcceleration: hw };
            if (c.mux === 'avc') cfg.avc = { format: 'avc' };
            try {
                const r = await VideoEncoder.isConfigSupported(cfg);
                if (r && r.supported) return { mux: c.mux, config: r.config || cfg, hardware: hw === 'prefer-hardware' };
            } catch (_) { /* next */ }
        }
    }
    return null;
}

export class Capture {
    /**
     * video, canvas — the page's elements (canvas is the preview AND the source)
     * stamp()      → {now, mps, unit, lat, lon, text} for this frame
     * stampOn()    → which parts are on
     * stampParts(st, on) → parts (overlay.stampParts bound to a clock)
     * onRecord(rec), onConfig(kind, cfg), onError(err), onStats(stats)
     */
    constructor(o) {
        this.video = o.video;
        this.canvas = o.canvas;
        this.ctx = this.canvas.getContext('2d', { alpha: false, desynchronized: true }) || this.canvas.getContext('2d');
        this.stamp = o.stamp;
        this.stampOn = o.stampOn;
        this.stampParts = o.stampParts;
        this.onRecord = o.onRecord || (() => {});
        this.onConfig = o.onConfig || (() => {});
        this.onError = o.onError || (() => {});
        this.onStats = o.onStats || (() => {});
        this.telemetry = o.telemetry || (() => {});
        this.fps = 30;
        this.timer = null;
        this.enc = null;
        this.encoding = false;
        this.layout = null;
        this.t0 = 0;
        this.audio = null;
        this.stats = { frames: 0, dropped: 0, bytes: 0, since: 0, lastFps: 0 };
    }

    /** Resize the canvas to the output size (called before recording and on settings change). */
    size(w, h) {
        if (this.canvas.width !== w) this.canvas.width = w;
        if (this.canvas.height !== h) this.canvas.height = h;
    }

    /** Draw the current camera frame letterboxed into the canvas, then the stamp. */
    draw() {
        const v = this.video, c = this.canvas, ctx = this.ctx;
        const W = c.width, H = c.height;
        if (!(v.readyState >= 2 && v.videoWidth)) return false;
        const vw = v.videoWidth, vh = v.videoHeight;
        const k = Math.min(W / vw, H / vh);
        const dw = Math.round(vw * k), dh = Math.round(vh * k);
        const dx = Math.round((W - dw) / 2), dy = Math.round((H - dh) / 2);
        if (dw !== W || dh !== H) { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H); }
        ctx.drawImage(v, dx, dy, dw, dh);
        const st = this.stamp();
        this.layout = drawStamp(ctx, W, H, this.stampParts(st, this.stampOn()));
        return true;
    }

    /** Start the pump. Without an encoder it only refreshes the preview (at a lower rate). */
    startPump() {
        if (this.timer) return;
        let next = performance.now();
        const loop = () => {
            this.timer = null;
            const fps = this.encoding ? this.fps : 15;
            const period = 1000 / fps;
            const now = performance.now();
            try {
                if (this.draw() && this.encoding) this.encodeFrame(now);
            } catch (e) {
                this.onError(e);
            }
            next += period;
            // a stall (a long task, a GC) — do not try to catch up frame by frame
            if (next < now - period * 2) next = now + period;
            this.timer = setTimeout(loop, Math.max(0, next - performance.now()));
        };
        this.timer = setTimeout(loop, 0);
    }

    stopPump() { if (this.timer) { clearTimeout(this.timer); this.timer = null; } }

    /** The next frame is encoded as a keyframe (a new segment has to start on one). */
    requestKey() { this.wantKey = true; }

    encodeFrame(now) {
        const enc = this.enc;
        if (!enc || enc.state !== 'configured') return;
        const ts = Math.round((now - this.t0) * 1000);
        if (ts <= this.lastTs) return;
        // the encoder cannot keep up (a hot phone throttling): drop, do not queue —
        // a queue that grows is memory that grows, and the lag would reach the loop
        if (enc.encodeQueueSize > 3) { this.stats.dropped++; return; }
        const key = this.lastKey < 0 || this.wantKey || ts - this.lastKey >= KEY_EVERY_US;
        this.wantKey = false;
        const frame = new VideoFrame(this.canvas, { timestamp: ts, duration: Math.round(1e6 / this.fps) });
        try { enc.encode(frame, { keyFrame: key }); } finally { frame.close(); }
        if (key) this.lastKey = ts;
        this.lastTs = ts;
        this.stats.frames++;
        const sec = Math.floor(now / 1000);
        if (sec !== this.stats.since) {
            this.stats.lastFps = this.stats.framesThisSec || 0;
            this.stats.framesThisSec = 0;
            this.stats.since = sec;
            try { this.onStats({ fps: this.stats.lastFps, dropped: this.stats.dropped, queue: enc.encodeQueueSize, frames: this.stats.frames }); } catch (_) {}
        }
        this.stats.framesThisSec = (this.stats.framesThisSec || 0) + 1;
    }

    /**
     * Configure the encoder and begin. t0 — performance.now() the session's
     * timestamps count from. → {mux, codec, hardware} or throws.
     */
    async startEncoding({ fps, bitrate, t0 }) {
        const w = this.canvas.width, h = this.canvas.height;
        this.fps = fps || 30;
        const pick = await pickVideoConfig(w, h, this.fps, bitrate);
        if (!pick) { const e = new Error('no video encoder'); e.code = 'no_encoder'; throw e; }
        this.t0 = t0;
        this.lastKey = -1;
        this.lastTs = -1;
        this.stats = { frames: 0, dropped: 0, bytes: 0, since: 0, lastFps: 0 };
        const frameUs = Math.round(1e6 / this.fps);
        // Chunks → loop records: the session's first configuration kept, parameter
        // sets in-band on every keyframe from the first change on — videoTap in
        // loop-writer.js (OpenH264 in Chromium < 130 changes them on every keyframe).
        const tap = videoTap({
            mux: pick.mux, frameUs,
            onConfig: (c) => this.onConfig('video', { codec: pick.mux, codecString: c.codecString || pick.config.codec, width: w, height: h, fps: this.fps, description: c.description, hardware: pick.hardware, changed: c.changed }),
            onRecord: (rec) => { this.stats.bytes += rec.data.byteLength; this.onRecord(rec); },
        });
        this.enc = new VideoEncoder({
            output: (chunk, meta) => tap(chunk, meta),
            error: (e) => { this.encoding = false; this.onError(e); },
        });
        this.enc.configure(pick.config);
        this.encoding = true;
        return { mux: pick.mux, codec: pick.config.codec, hardware: pick.hardware };
    }

    /**
     * Sound from a microphone stream. → {codec, sampleRate} or null when this
     * browser cannot encode sound (the recording goes on without it).
     */
    async startAudio(stream, { workletUrl, t0 }) {
        const Door = typeof window !== 'undefined' ? window.AacAccessUnit : null;
        if (!stream || !Door || typeof AudioEncoder === 'undefined' || typeof AudioData === 'undefined') return null;
        let ctx;
        try { ctx = new AudioContext({ sampleRate: 48000 }); } catch (_) { ctx = new AudioContext(); }
        const sr = ctx.sampleRate;
        let chosen = null;
        for (const c of [
            { mux: 'aac', cfg: { codec: 'mp4a.40.2', sampleRate: sr, numberOfChannels: 1, bitrate: 64000 } },
            { mux: 'opus', cfg: { codec: 'opus', sampleRate: 48000, numberOfChannels: 1, bitrate: 48000 } },
        ]) {
            if (c.mux === 'opus' && sr !== 48000) continue;
            try { const r = await AudioEncoder.isConfigSupported(c.cfg); if (r && r.supported) { chosen = c; break; } } catch (_) {}
        }
        if (!chosen) { try { ctx.close(); } catch (_) {} return null; }
        await ctx.audioWorklet.addModule(workletUrl);
        if (ctx.state === 'suspended') { try { await ctx.resume(); } catch (_) {} }
        const src = ctx.createMediaStreamSource(stream);
        const node = new AudioWorkletNode(ctx, 'pcm-capture');
        const mute = ctx.createGain(); mute.gain.value = 0;
        src.connect(node); node.connect(mute); mute.connect(ctx.destination);
        const atap = audioTap({
            onConfig: (c) => this.onConfig('audio', { codec: chosen.mux, codecString: c.codecString || chosen.cfg.codec, sampleRate: c.sampleRate || chosen.cfg.sampleRate, channels: c.channels || 1, description: c.description }),
            onRecord: (rec) => this.onRecord(rec),
        });
        const enc = Door.encoder({
            output: (chunk, meta) => atap(chunk, meta),
            error: (e) => { this.telemetry('audio_encoder_error', { err: String((e && e.message) || e).slice(0, 80) }); },
        }, { slug: 'dashcam' });
        enc.configure(chosen.cfg);

        // The sample clock and the page clock drift apart over a long drive; the
        // batch's arrival bounds where it really began (arrival only ever adds
        // delay), so the smallest recent offset re-anchors the sample clock.
        let base = null, sent = 0, lastEnd = -1;
        const offs = [];
        node.port.onmessage = (ev) => {
            const d = ev.data;
            if (!d || d.type !== 'pcm' || !this.encoding || enc.state !== 'configured') return;
            const n = d.samples, rate = d.sr || sr;
            const arrival = (performance.now() - t0) * 1000 - n / rate * 1e6;
            if (base === null) base = arrival;
            let ts = base + sent / rate * 1e6;
            offs.push(arrival - ts);
            if (offs.length > 60) offs.shift();
            const drift = Math.min.apply(null, offs);
            if (offs.length >= 20 && Math.abs(drift) > 40000) { base += drift; offs.length = 0; ts = base + sent / rate * 1e6; }
            if (ts < lastEnd) ts = lastEnd;          // never backwards: the muxer refuses that
            if (ts < 0) { sent += n; return; }
            const ch = d.channels[0];
            try {
                const ad = new AudioData({ format: 'f32-planar', sampleRate: rate, numberOfFrames: n, numberOfChannels: 1, timestamp: Math.round(ts), data: ch });
                enc.encode(ad);
                ad.close();
            } catch (e) { this.telemetry('audio_frame_error', { err: String((e && e.message) || e).slice(0, 80) }); }
            sent += n;
            lastEnd = ts + n / rate * 1e6;
        };
        this.audio = { ctx, node, src, enc };
        return { codec: chosen.mux, sampleRate: chosen.cfg.sampleRate };
    }

    /** Finish: flush what the encoders still hold (bounded — a frozen page must not hang the stop). */
    async stopEncoding() {
        this.encoding = false;
        const waits = [];
        const enc = this.enc; this.enc = null;
        if (enc && enc.state === 'configured') waits.push(enc.flush().catch(() => {}).then(() => { try { enc.close(); } catch (_) {} }));
        const a = this.audio; this.audio = null;
        if (a) {
            try { a.node.port.onmessage = null; a.node.port.postMessage({ cmd: 'stop' }); } catch (_) {}
            try { a.src.disconnect(); } catch (_) {}
            if (a.enc.state === 'configured') waits.push(a.enc.flush().catch(() => {}).then(() => { try { a.enc.close(); } catch (_) {} }));
            waits.push(Promise.resolve().then(() => a.ctx.close()).catch(() => {}));
        }
        await Promise.race([Promise.all(waits), new Promise(r => setTimeout(r, 3000))]);
    }
}
